import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { getMe, login as apiLogin, logout as apiLogout } from '../api';
import { markSession, setAuthHandlers } from '../api/client';
import { queryKeys } from '../hooks/queries/queryKeys';

export const AuthContext = createContext();

// Tells the app's other tabs about a login or logout at once, rather than on
// their next 401
const CHANNEL_NAME = 'cp-auth';
const openChannel = () =>
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

const getMeOrNull = async () => {
  try {
    const user = await getMe();
    markSession(true);
    return user;
  } catch (err) {
    if (err.status === 401) return null;
    throw err;
  }
};

/**
 * The one place that knows who is logged in. The session lives in HttpOnly
 * cookies the app can't read, so `GET /me` is the source of truth: a user, or
 * null when logged out. Components use `useAuth().isLoggedIn` and the `mine`
 * fields in API responses; nothing reads cookies.
 */
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.me,
    queryFn: getMeOrNull,
    staleTime: Infinity,
    retry: false,
  });

  // Viewer fields (`mine`, `my_rating`, `liked_by_me`) are cached all over, so
  // a change of user drops everything else and refetches what's on screen
  const clearSession = useCallback(() => {
    queryClient.setQueryData(queryKeys.me, null);
    queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
  }, [queryClient]);

  const channel = useMemo(openChannel, []);

  useEffect(() => {
    if (!channel) return undefined;
    channel.onmessage = (event) => {
      if (event.data === 'logout') {
        markSession(false);
        clearSession();
      } else if (event.data === 'login') {
        markSession(true);
        queryClient.invalidateQueries();
      }
    };
    return () => channel.close();
  }, [channel, clearSession, queryClient]);

  const pathRef = React.useRef(location);
  pathRef.current = location;

  useEffect(() => {
    setAuthHandlers({
      onSessionLost: clearSession,
      // A write needed a session that's gone: log in, then come back here
      onLoginRequired: () => {
        const { pathname, search } = pathRef.current;
        if (pathname !== '/login') {
          navigate(`/login?next=${encodeURIComponent(pathname + search)}`);
        }
      },
    });
  }, [clearSession, navigate]);

  const login = useCallback(
    async (email, password) => {
      await apiLogin({ email, password });
      markSession(true);
      // A different user may have been cached before
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
      // staleTime 0: the cached value is the logged-out null
      await queryClient.fetchQuery({ queryKey: queryKeys.me, queryFn: getMeOrNull, staleTime: 0 });
      if (channel) channel.postMessage('login');
    },
    [channel, queryClient]
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      markSession(false);
      clearSession();
      if (channel) channel.postMessage('logout');
    }
  }, [channel, clearSession]);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.me }),
    [queryClient]
  );

  const value = useMemo(
    () => ({ user: user || null, isLoggedIn: Boolean(user), isLoading, login, logout, refresh }),
    [user, isLoading, login, logout, refresh]
  );

  // Until GET /me settles (including a silent refresh after time away), so
  // screens don't flash the logged-out view
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
